/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './CheckboxColumn';
import { helpRenders } from './CheckboxColumn_Help';

const CheckboxColumnManager: ComponentManagerFn<'CheckboxColumn'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'CheckboxColumn'>(platform, 'CheckboxColumn'),
    type: 'CheckboxColumn',
    name: 'Checkbox Column',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ColumnFooter',
          'TriggerOnChange',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...baseColumnProps.viewAttribute,
        aliasType: ({ appUid, pageId, itemId }) => {
          const parent = platform.findParentOfType(
            appUid,
            pageId,
            itemId,
            'LOVGrid',
          );
          return parent ? 'LOV' : 'STORE';
        },
        getAttributeDataTypes: ({ props }) => {
          return ['YN', 'Boolean', 'TF', 'String'];
        },
      },
      checkedValue: {
        type: 'string',
        label: 'Checked Value',
        maxLength: 1,
      },
      unCheckedValue: {
        type: 'string',
        label: 'Unchecked Value',
        maxLength: 1,
      },
      displayAsSwitch: {
        label: 'Display As Switch',
        type: 'boolean',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
    },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }

      switch (prop) {
        case 'required':
          return false;
        default:
          return true;
      }
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    replaceableWith: ['StringColumn', 'LookupSelectColumn', 'SelectColumn'],
    category: 'Data Grid',
    icon: { iconName: 'check-square' },
    description: `**Overview**<br/>
The Checkbox Column is a data entry column that renders a boolean value as a standard checkbox or toggle switch within a Data Grid.
It is designed to handle binary choices (True/False, Yes/No, Active/Inactive). Users can toggle the state directly in the grid without needing to open a separate edit form, making it ideal for "Bulk Actions" or "Quick Toggles."

**1. Data Binding & Mapping**<br/>
This column connects to a specific boolean-compatible attribute in your Data Store.
* **View Attribute:** Select the data field. (Supported Types: Boolean, YN, TF, String).
* **Value Mapping:** If your database stores something other than standard booleans (e.g., 'Y'/'N'), you can map these values explicitly.
    * **Checked Value:** The value to save when the box is checked (e.g., 'Y').
    * **Unchecked Value:** The value to save when the box is unchecked (e.g., 'N').

**2. Visual Style**<br/>
* **Display As Switch:**
    * **Enabled (True):** Renders as a modern "Toggle Switch" (slider). Best for settings like "Enable Notifications."
    * **Disabled (False):** Renders as a standard square checkbox. Best for bulk selection or checklists.

**3. Interaction & Validation**<br/>
Since toggling a checkbox is an instant action, you often need immediate feedback or validation.
* **TriggerOnChange:** Add this child component to execute logic immediately after the user toggles the value. (Use Case: If a user unchecks "Active," trigger a workflow to disable related fields in the same row).
* **Server Validate On Change:** If enabled, every toggle triggers a server-side validation call. If the server rejects the change (e.g., "Cannot deactivate a primary admin"), the checkbox automatically reverts to its previous state.

**4. Usage Guidelines**<br/>
* **Read-Only Mode:** If the Data Grid is set to **Updatable: False**, the checkboxes will render in a disabled (greyed out) state, allowing users to see the values but not change them.`,
    helpRenders,
  };
};

export default CheckboxColumnManager;
